import { FunctionComponent, useState, useMemo, useEffect } from "react"
import {
  useGetAllUsersByorganizationIdQuery,
  useBulkReminderMutation,
} from "../../app/services/meseeknaApi"
import {
  useLazyGetDataExplorerUserDetailsQuery,
  DataExplorerUserMicrolearning,
} from "../../app/services/meseeknaApiV2"
import { RootState, useTypedSelector } from "../../app/store"
import { useOrganizationRecovery } from "../../hooks/useOrganizationRecovery"

type MicrolearningEnrollment = {
  measureId: number
  measureName: string
  status: string
  daysInProgram: number
  completionPercent: number
  startedAt: string
}

type TeamMember = {
  id: number
  firstName: string | null
  lastName: string | null
  email: string
  simulationStatus: "not_started" | "in_progress" | "completed"
  enrollments: MicrolearningEnrollment[]
  isLoadingDetails: boolean
}

const RecruiterMicrolearningDashboard: FunctionComponent = () => {
  const [selectedUsers, setSelectedUsers] = useState<number[]>([])
  const [reminderSending, setReminderSending] = useState(false)
  const [reminderMessage, setReminderMessage] = useState("")
  const [filterStatus, setFilterStatus] = useState<"all" | "enrolled" | "not_enrolled" | "completed">("all")
  const [userDetails, setUserDetails] = useState<Record<number, { enrollments: MicrolearningEnrollment[]; loaded: boolean }>>({})

  const authStore = useTypedSelector((state: RootState) => state.authSlice)
  const { isRecovering } = useOrganizationRecovery()

  const effectiveOrgId = Number(
    authStore.organization?.id || authStore.user?.organizationId || 0,
  )

  // Fetch all users in the organization
  const { data: usersData, isLoading: isLoadingUsers } = useGetAllUsersByorganizationIdQuery(
    { body: { organizationId: effectiveOrgId } },
    { skip: !effectiveOrgId || isRecovering }
  )

  const [fetchUserDetails] = useLazyGetDataExplorerUserDetailsQuery()
  const [sendReminder] = useBulkReminderMutation()

  // Calculate days between two dates
  const calculateDaysInProgram = (startDate: string): number => {
    const start = new Date(startDate)
    const now = new Date()
    const diffTime = Math.abs(now.getTime() - start.getTime())
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  }

  // Estimate completion percent based on status and days
  const estimateCompletion = (status: string, daysInProgram: number): number => {
    if (status === "completed") return 100
    // Assume a 90-day program, scale based on days
    const programDuration = 90
    const baseProgress = Math.min((daysInProgram / programDuration) * 100, 95)
    return Math.round(baseProgress)
  }

  // Process microlearning data from user details
  const processMicrolearning = (microlearning: DataExplorerUserMicrolearning[]): MicrolearningEnrollment[] => {
    return microlearning.map((ml) => {
      const daysInProgram = calculateDaysInProgram(ml.createdAt)
      return {
        measureId: ml.measureId,
        measureName: ml.measureName,
        status: ml.status,
        daysInProgram,
        completionPercent: estimateCompletion(ml.status, daysInProgram),
        startedAt: ml.createdAt,
      }
    })
  }

  // Fetch user details for completed users
  useEffect(() => {
    if (!usersData?.data) return

    const completedUsers = usersData.data.filter(
      (user) => user.role === 4 && user.dateCompleted && !userDetails[user.id || 0]?.loaded
    )

    // Fetch details for first 10 users to avoid too many requests
    const usersToFetch = completedUsers.slice(0, 10)

    usersToFetch.forEach(async (user) => {
      if (!user.id) return

      try {
        const result = await fetchUserDetails(user.id).unwrap()
        if (result.data?.microlearning) {
          setUserDetails((prev) => ({
            ...prev,
            [user.id as number]: {
              enrollments: processMicrolearning(result.data.microlearning),
              loaded: true,
            },
          }))
        }
      } catch (err) {
        console.warn(`Failed to fetch details for user ${user.id}:`, err)
        setUserDetails((prev) => ({
          ...prev,
          [user.id as number]: { enrollments: [], loaded: true },
        }))
      }
    })
  }, [usersData, userDetails, fetchUserDetails])

  // Process users data
  const teamMembers: TeamMember[] = useMemo(() => {
    if (!usersData?.data) return []

    return usersData.data
      .filter((user) => user.role === 4)
      .map((user) => {
        const hasCompleted = !!user.dateCompleted
        const details = userDetails[user.id || 0]

        return {
          id: user.id || 0,
          firstName: user.firstName || null,
          lastName: user.lastName || null,
          email: user.email || "",
          simulationStatus: hasCompleted ? "completed" : (user.savepoint ? "in_progress" : "not_started"),
          enrollments: details?.enrollments || [],
          isLoadingDetails: hasCompleted && !details?.loaded,
        } as TeamMember
      })
  }, [usersData, userDetails])

  // Filter members based on status
  const filteredMembers = useMemo(() => {
    if (filterStatus === "all") return teamMembers
    if (filterStatus === "enrolled") return teamMembers.filter((m) => m.enrollments.length > 0)
    if (filterStatus === "not_enrolled") return teamMembers.filter((m) => m.enrollments.length === 0 && m.simulationStatus === "completed")
    if (filterStatus === "completed") return teamMembers.filter((m) => m.enrollments.some((e) => e.status === "completed"))
    return teamMembers
  }, [teamMembers, filterStatus])

  // Stats
  const stats = useMemo(() => {
    const total = teamMembers.length
    const simulationsCompleted = teamMembers.filter((m) => m.simulationStatus === "completed").length
    const microlearningEnrolled = teamMembers.filter((m) => m.enrollments.length > 0).length
    const microlearningCompleted = teamMembers.filter((m) => m.enrollments.some((e) => e.status === "completed")).length
    const avgCompletion = microlearningEnrolled > 0
      ? Math.round(
          teamMembers
            .filter((m) => m.enrollments.length > 0)
            .reduce((sum, m) => sum + Math.max(...m.enrollments.map((e) => e.completionPercent), 0), 0) / microlearningEnrolled
        )
      : 0

    return {
      total,
      simulationsCompleted,
      simulationCompletionRate: total > 0 ? Math.round((simulationsCompleted / total) * 100) : 0,
      microlearningEnrolled,
      microlearningEnrollmentRate: simulationsCompleted > 0 ? Math.round((microlearningEnrolled / simulationsCompleted) * 100) : 0,
      microlearningCompleted,
      avgCompletion,
    }
  }, [teamMembers])

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredMembers.length) {
      setSelectedUsers([])
    } else {
      setSelectedUsers(filteredMembers.map((m) => m.id))
    }
  }

  const handleSelectUser = (userId: number) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    )
  }

  const handleSendReminder = async () => {
    if (selectedUsers.length === 0) return

    setReminderSending(true)
    try {
      const selectedEmails = filteredMembers
        .filter((m) => selectedUsers.includes(m.id))
        .map((m) => ({ email: m.email }))

      await sendReminder({
        body: {
          status: "microlearning_reminder",
          orgId: effectiveOrgId,
          users: selectedEmails,
        },
      })

      setReminderMessage("Reminders sent successfully!")
      setSelectedUsers([])
      setTimeout(() => setReminderMessage(""), 3000)
    } catch {
      setReminderMessage("Failed to send reminders. Please try again.")
      setTimeout(() => setReminderMessage(""), 3000)
    } finally {
      setReminderSending(false)
    }
  }

  const getEnrollmentBadge = (enrollments: MicrolearningEnrollment[]) => {
    if (enrollments.length === 0) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Not Enrolled</span>
    }
    const hasCompleted = enrollments.some((e) => e.status === "completed")
    if (hasCompleted) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">Completed</span>
    }
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-teal-100 text-teal-700">In Progress</span>
  }

  if (isLoadingUsers) {
    return (
      <div className="min-h-full bg-gray-50 py-8 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="grid grid-cols-4 gap-4 mb-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-24 bg-gray-200 rounded"></div>
              ))}
            </div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-full bg-gray-50 py-8 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Team Development Dashboard</h1>
          <p className="text-gray-600 mt-2">
            Track your team&apos;s progress through targeted development programs based on their simulation results.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Team Members</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">In Development</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.microlearningEnrolled}</p>
                <p className="text-xs text-gray-400 mt-1">{stats.microlearningEnrollmentRate}% of eligible</p>
              </div>
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Avg. Completion</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.avgCompletion}%</p>
                <p className="text-xs text-gray-400 mt-1">across enrolled</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Completed Training</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.microlearningCompleted}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="all">All Members</option>
                <option value="enrolled">Enrolled in Development</option>
                <option value="not_enrolled">Not Yet Enrolled</option>
                <option value="completed">Completed Training</option>
              </select>
              <span className="text-sm text-gray-500">
                {filteredMembers.length} member{filteredMembers.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="flex items-center gap-3">
              {selectedUsers.length > 0 && (
                <span className="text-sm text-gray-600">
                  {selectedUsers.length} selected
                </span>
              )}
              <button
                onClick={handleSendReminder}
                disabled={selectedUsers.length === 0 || reminderSending}
                className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {reminderSending ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    Send Reminder
                  </>
                )}
              </button>
            </div>
          </div>
          {reminderMessage && (
            <div className={`mt-3 p-3 rounded-lg text-sm ${reminderMessage.includes("success") ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
              {reminderMessage}
            </div>
          )}
        </div>

        {/* Help Section - How Team Development Works */}
        <div className="mb-6 bg-teal-50 rounded-xl border border-teal-200 p-6">
          <h3 className="text-lg font-semibold text-teal-900 mb-2">How Team Development Works</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-teal-800">
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-teal-200 rounded-full flex items-center justify-center text-teal-700 font-bold">1</span>
              <p>Team members complete their cognitive simulation to reveal their behavioral profile and growth areas.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-teal-200 rounded-full flex items-center justify-center text-teal-700 font-bold">2</span>
              <p>Each person is enrolled in a 90-day development track targeting specific measures based on their results.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-teal-200 rounded-full flex items-center justify-center text-teal-700 font-bold">3</span>
              <p>Track their progress here, and send reminders to keep them engaged with their learning journey.</p>
            </div>
          </div>
        </div>

        {/* Team Members Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === filteredMembers.length && filteredMembers.length > 0}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  />
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Team Member
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Measure / Focus Area
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Days in Program
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMembers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    No team members found matching the selected filter.
                  </td>
                </tr>
              ) : (
                filteredMembers.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(member.id)}
                        onChange={() => handleSelectUser(member.id)}
                        className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-teal-100 rounded-full flex items-center justify-center">
                          <span className="text-teal-700 font-medium text-sm">
                            {(member.firstName?.[0] || "").toUpperCase()}{(member.lastName?.[0] || "").toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {member.firstName} {member.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{member.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {member.isLoadingDetails ? (
                        <div className="animate-pulse h-4 bg-gray-200 rounded w-24"></div>
                      ) : member.enrollments.length > 0 ? (
                        <div className="space-y-1">
                          {member.enrollments.slice(0, 2).map((enrollment, idx) => (
                            <span
                              key={idx}
                              className="inline-block px-2 py-1 text-xs bg-teal-50 text-teal-700 rounded mr-1"
                            >
                              {enrollment.measureName}
                            </span>
                          ))}
                          {member.enrollments.length > 2 && (
                            <span className="text-xs text-gray-500">
                              +{member.enrollments.length - 2} more
                            </span>
                          )}
                        </div>
                      ) : member.simulationStatus === "completed" ? (
                        <span className="text-sm text-gray-400">Awaiting enrollment</span>
                      ) : (
                        <span className="text-sm text-gray-400">Complete simulation first</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {member.enrollments.length > 0 ? (
                        <span className="text-sm text-gray-900">
                          {Math.max(...member.enrollments.map((e) => e.daysInProgram))} days
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {member.enrollments.length > 0 ? (
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-teal-500 rounded-full transition-all"
                              style={{ width: `${Math.max(...member.enrollments.map((e) => e.completionPercent))}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600">
                            {Math.max(...member.enrollments.map((e) => e.completionPercent))}%
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {getEnrollmentBadge(member.enrollments)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default RecruiterMicrolearningDashboard
